import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  PERSONA_SCRIPT_GUIDELINES_MAX_CHARS,
  REFERENCE_VIDEO_TOO_LONG,
} from "@nexus/contracts";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  Textarea,
  cn,
} from "@nexus/ui";

import { ApiError } from "@/lib/api-client";
import { useGeneratePersonaOpportunityScripts } from "@/lib/queries/persona-opportunity-scripts.queries";

import { extractPersonaSetGuidelines } from "../../../api/persona-opportunity-scripts.api.js";
import { usePersonaScriptReferenceUpload } from "../../../hooks/usePersonaScriptReferenceUpload.js";

import type {
  PersonaScriptOutputType,
  PersonaScriptVideoReference,
} from "../../../api/persona-opportunity-scripts.api.js";

const DEFAULT_SCRIPTS_PER_ANGLE = 5;
const MIN_SCRIPTS_PER_ANGLE = 1;
const MAX_SCRIPTS_PER_ANGLE = 10;
const GUIDELINES_MAX_BYTES = 5 * 1024 * 1024;
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export interface GenerateScriptsAngle {
  id: string;
  angle: string;
  personaTitle?: string;
  supportingAd: { thumbnailUrl: string | null } | null;
}

export interface GenerateScriptsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  angles: GenerateScriptsAngle[];
  onAccepted: () => void;
}

type ReferenceMode = "none" | "example" | "upload";

function resolveOutputType(
  wantScript: boolean,
  wantBrief: boolean,
): PersonaScriptOutputType | null {
  if (wantScript && wantBrief) return "both";
  if (wantScript) return "script";
  if (wantBrief) return "brief";
  return null;
}

function isLegacyDoc(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "application/msword" || (name.endsWith(".doc") && !name.endsWith(".docx"));
}

function isAllowedGuidelinesFile(file: File): boolean {
  if (isLegacyDoc(file)) return false;
  const name = file.name.toLowerCase();
  return (
    file.type === "text/plain" ||
    name.endsWith(".txt") ||
    file.type === "application/pdf" ||
    name.endsWith(".pdf") ||
    file.type === DOCX_MIME ||
    name.endsWith(".docx")
  );
}

function mergeGuidelines(existing: string, extracted: string): string {
  const next = existing.trim() ? `${existing.trim()}\n\n${extracted.trim()}` : extracted.trim();
  return next.slice(0, PERSONA_SCRIPT_GUIDELINES_MAX_CHARS);
}

function useObjectUrl(file: File | null | undefined): string | undefined {
  const [url, setUrl] = React.useState<string>();

  React.useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url;
}

function personaInitials(name: string): string {
  return name
    .replace(/^The\s+/i, "")
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

export function GenerateScriptsModal({
  open,
  onOpenChange,
  setId,
  tenantId,
  angles,
  onAccepted,
}: GenerateScriptsModalProps) {
  const { t } = useTranslation();
  const upload = usePersonaScriptReferenceUpload({ tenantId, t });
  const videoInputRef = React.useRef<HTMLInputElement>(null);
  const guidelinesInputRef = React.useRef<HTMLInputElement>(null);
  const extractRunIdRef = React.useRef(0);

  const [wantScript, setWantScript] = React.useState(true);
  const [wantBrief, setWantBrief] = React.useState(false);
  const [referenceMode, setReferenceMode] = React.useState<ReferenceMode>(() =>
    angles.some((angle) => angle.supportingAd != null) ? "example" : "none",
  );
  const [guidelines, setGuidelines] = React.useState("");
  const [guidelinesUploadId, setGuidelinesUploadId] = React.useState<string | null>(null);
  const [guidelinesError, setGuidelinesError] = React.useState<string | null>(null);
  const [isExtracting, setIsExtracting] = React.useState(false);
  const [scriptsPerAngle, setScriptsPerAngle] = React.useState(DEFAULT_SCRIPTS_PER_ANGLE);
  const [durationError, setDurationError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const generate = useGeneratePersonaOpportunityScripts({
    onAccepted: () => {
      setIsSubmitting(false);
      onOpenChange(false);
      onAccepted();
    },
  });

  const hasSupportingAd = angles.some((angle) => angle.supportingAd != null);

  React.useEffect(() => {
    if (!open) {
      extractRunIdRef.current += 1;
      upload.reset();
      return;
    }
    setWantScript(true);
    setWantBrief(false);
    setReferenceMode(hasSupportingAd ? "example" : "none");
    setGuidelines("");
    setGuidelinesUploadId(null);
    setGuidelinesError(null);
    setIsExtracting(false);
    setScriptsPerAngle(DEFAULT_SCRIPTS_PER_ANGLE);
    setDurationError(null);
    setIsSubmitting(false);
    upload.reset();
  }, [open, hasSupportingAd, upload.reset]);

  const outputType = resolveOutputType(wantScript, wantBrief);
  const scriptCount = wantScript ? angles.length * scriptsPerAngle : 0;
  const briefCount = wantBrief ? angles.length : 0;
  const uploadBusy = upload.state === "uploading" || upload.state === "confirming";
  const uploadReady = referenceMode === "upload" && upload.state === "ready" && !!upload.uploadId;
  const generateDisabled =
    !outputType ||
    isSubmitting ||
    isExtracting ||
    (referenceMode === "upload" && !uploadReady);

  const referenceError =
    durationError ?? (referenceMode === "upload" ? upload.error : null);

  const handleOpenChange = (next: boolean) => {
    if (!next && isSubmitting) return;
    onOpenChange(next);
  };

  const handleSelectExample = () => {
    setDurationError(null);
    if (referenceMode === "example") {
      setReferenceMode("none");
      return;
    }
    upload.reset();
    setReferenceMode("example");
  };

  const handlePickVideo = (file: File | undefined) => {
    if (!file) return;
    setDurationError(null);
    setReferenceMode("upload");
    upload.start(file);
  };

  const handleClearVideo = () => {
    setDurationError(null);
    upload.reset();
    setReferenceMode("none");
  };

  const handleGuidelinesFile = async (file: File | undefined) => {
    if (!file) return;
    setGuidelinesError(null);
    if (isLegacyDoc(file)) {
      setGuidelinesError(
        t(
          "personaOpportunities.generateScripts.guidelines.legacyDoc",
          "Legacy .doc files are not supported — convert to DOCX first.",
        ),
      );
      return;
    }
    if (!isAllowedGuidelinesFile(file)) {
      setGuidelinesError(
        t(
          "personaOpportunities.generateScripts.guidelines.invalidType",
          "Upload a PDF, DOCX, or TXT file.",
        ),
      );
      return;
    }
    if (file.size > GUIDELINES_MAX_BYTES) {
      setGuidelinesError(
        t(
          "personaOpportunities.generateScripts.guidelines.tooLarge",
          "Guidelines file must be 5MB or smaller.",
        ),
      );
      return;
    }
    const runId = ++extractRunIdRef.current;
    setIsExtracting(true);
    try {
      const extracted = await extractPersonaSetGuidelines(setId, file, tenantId);
      if (extractRunIdRef.current !== runId) return;
      setGuidelines((current) => mergeGuidelines(current, extracted.text));
      setGuidelinesUploadId(extracted.uploadId);
    } catch {
      if (extractRunIdRef.current !== runId) return;
      setGuidelinesUploadId(null);
      setGuidelinesError(
        t(
          "personaOpportunities.generateScripts.guidelines.extractFailed",
          "Could not read that file. Try another PDF, DOCX, or TXT.",
        ),
      );
    } finally {
      if (extractRunIdRef.current === runId) setIsExtracting(false);
    }
  };

  const buildVideoReference = (): PersonaScriptVideoReference => {
    if (referenceMode === "example") return { kind: "angle_example" };
    if (referenceMode === "upload" && upload.uploadId) {
      return { kind: "upload", uploadId: upload.uploadId };
    }
    return { kind: "none" };
  };

  const handleGenerate = () => {
    if (!outputType || generateDisabled) return;
    setDurationError(null);
    setIsSubmitting(true);
    const trimmedGuidelines = guidelines.trim();
    generate.mutate(
      {
        setId,
        tenantId,
        personaOpportunityIds: angles.map((angle) => angle.id),
        outputType,
        ...(wantScript ? { scriptsPerAngle } : {}),
        videoReference: buildVideoReference(),
        ...(trimmedGuidelines ? { scriptGuidelines: trimmedGuidelines } : {}),
        ...(trimmedGuidelines && guidelinesUploadId ? { guidelinesUploadId } : {}),
      },
      {
        onError: (error) => {
          setIsSubmitting(false);
          if (error instanceof ApiError && error.code === REFERENCE_VIDEO_TOO_LONG) {
            setDurationError(
              t(
                "personaOpportunities.generateScripts.reference.tooLong",
                "Reference video must be 60 seconds or shorter.",
              ),
            );
          }
        },
      },
    );
  };

  const modalTitle = !outputType
    ? t("personaOpportunities.generateScripts.titleEmpty", "Generate")
    : outputType === "both"
      ? t("personaOpportunities.generateScripts.titleBoth", "Generate Scripts & Briefs")
      : outputType === "brief"
        ? t("personaOpportunities.generateScripts.titleBriefs", "Generate Briefs")
        : t("personaOpportunities.generateScripts.title", "Generate Scripts");

  const submitLabel = (() => {
    if (!outputType) {
      return t("personaOpportunities.generateScripts.selectOutput", "Select an output type");
    }
    if (outputType === "both") {
      return t("personaOpportunities.generateScripts.submitBoth", {
        count: briefCount,
        scripts: scriptCount,
        defaultValue: "Generate {{scripts}} scripts + {{count}} briefs",
      });
    }
    if (outputType === "brief") {
      return briefCount === 1
        ? t("personaOpportunities.generateScripts.submitBriefs_one", "Generate brief")
        : t("personaOpportunities.generateScripts.submitBriefs", {
            count: briefCount,
            defaultValue: "Generate {{count}} briefs",
          });
    }
    return t("personaOpportunities.generateScripts.submitScripts", {
      count: scriptCount,
      defaultValue: "Generate {{count}} scripts",
    });
  })();

  const examplePosters = angles
    .filter((angle) => !!angle.supportingAd?.thumbnailUrl)
    .slice(0, 3)
    .map((angle) => ({
      id: angle.id,
      url: angle.supportingAd?.thumbnailUrl ?? "",
    }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="form"
        className="grid max-h-[94vh] grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[640px]"
        data-testid="generate-scripts-modal"
      >
        <DialogHeader className="border-b border-border px-6 pt-5 pr-12 pb-4">
          <DialogTitle className="text-[15px] font-semibold">
            {modalTitle}
          </DialogTitle>
          <DialogDescription className="mt-1 text-[13px] leading-relaxed">
            {angles.length === 1
              ? t(
                  "personaOpportunities.generateScripts.description",
                  "Adge writes for this angle using its evidence, persona, and your guidelines.",
                )
              : t(
                  "personaOpportunities.generateScripts.descriptionMany",
                  "Adge writes for each selected angle using its evidence, persona, and your guidelines.",
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-start gap-2.5 border-b border-border bg-muted px-6 py-3">
          <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
            {t("personaOpportunities.generateScripts.angleCount", {
              count: angles.length,
              defaultValue: "{{count}} angles",
            })}
          </span>
          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            {angles.map((angle) => {
              const initials = personaInitials(angle.personaTitle ?? "");
              return (
                <span
                  key={angle.id}
                  className="inline-flex max-w-[280px] items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs text-foreground"
                  title={angle.angle}
                >
                  {initials ? (
                    <span className="grid size-4 shrink-0 place-items-center rounded-full bg-muted text-[8px] font-semibold text-muted-foreground">
                      {initials}
                    </span>
                  ) : null}
                  <span className="truncate">{angle.angle}</span>
                </span>
              );
            })}
          </div>
        </div>

        <div className="relative flex min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-[18px] pb-6">
          <section>
            <FieldLabel
              label={t("personaOpportunities.generateScripts.outputType.label", "Output type")}
              required
              hint={t(
                "personaOpportunities.generateScripts.outputType.hint",
                "What Adge produces for each angle. Pick one or both.",
              )}
            />
            <div className="flex flex-wrap gap-2.5">
              <OutputTypeCard
                checked={wantScript}
                onCheckedChange={setWantScript}
                title={t("personaOpportunities.generateScripts.outputType.script", "Script")}
                hint={t(
                  "personaOpportunities.generateScripts.outputType.scriptHint",
                  "Scene-by-scene shooting script — visuals, voiceover, product and props.",
                )}
                testId="generate-scripts-output-script"
              />
              <OutputTypeCard
                checked={wantBrief}
                onCheckedChange={setWantBrief}
                title={t("personaOpportunities.generateScripts.outputType.brief", "Brief")}
                hint={t(
                  "personaOpportunities.generateScripts.outputType.briefHint",
                  "One per angle — concept, CTA, and 4 hooks to choose from.",
                )}
                testId="generate-scripts-output-brief"
              />
            </div>
          </section>

          <section className="mt-3.5 border-t border-border pt-3.5">
            <FieldLabel
              label={t("personaOpportunities.generateScripts.reference.label", "Video reference")}
              hint={t(
                "personaOpportunities.generateScripts.reference.hint",
                "Optional · Adge reuses the video's structure, hook, and pacing.",
              )}
            />
            <div
              role="checkbox"
              tabIndex={0}
              data-testid="generate-scripts-reference-example"
              aria-checked={referenceMode === "example"}
              onClick={handleSelectExample}
              onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") {
                  event.preventDefault();
                  handleSelectExample();
                }
              }}
              className={cn(
                "mb-2 flex w-full cursor-pointer items-start gap-2.5 rounded-sm p-3 text-left transition-colors",
                referenceMode === "example"
                  ? "border-[1.5px] border-primary bg-primary/5"
                  : "border border-border hover:bg-muted/60",
              )}
            >
              <Checkbox
                checked={referenceMode === "example"}
                tabIndex={-1}
                aria-hidden
                className="pointer-events-none mt-px"
              />
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block text-[13px] font-semibold",
                    referenceMode === "example" ? "text-primary" : "text-foreground",
                  )}
                >
                  {t(
                    "personaOpportunities.generateScripts.reference.example",
                    "Use creative example as video reference",
                  )}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                  {!hasSupportingAd
                    ? t(
                        "personaOpportunities.generateScripts.reference.exampleUnavailable",
                        "None of the selected angles have a creative example.",
                      )
                    : angles.length === 1
                      ? t(
                          "personaOpportunities.generateScripts.reference.exampleHint",
                          "Adge uses this angle's top creative as the video reference.",
                        )
                      : t(
                          "personaOpportunities.generateScripts.reference.exampleHintMany",
                          "Adge uses each angle's own top creative as its video reference.",
                        )}
                </span>
              </span>
              {examplePosters.length > 0 ? (
                <span className="flex shrink-0 gap-0.5">
                  {examplePosters.map((poster) => (
                    <img
                      key={poster.id}
                      src={poster.url}
                      alt={t(
                        "personaOpportunities.generateScripts.reference.exampleThumbnailAlt",
                        "Top creative thumbnail",
                      )}
                      className="h-8 w-6 rounded-[3px] object-cover"
                    />
                  ))}
                </span>
              ) : null}
            </div>

            <p className="mb-1.5 mt-2.5 text-xs text-muted-foreground">
              {t("personaOpportunities.generateScripts.reference.orUpload", "Or upload your own")}
            </p>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,.mp4"
              className="hidden"
              onChange={(event) => {
                handlePickVideo(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            {upload.file ? (
              <div
                className={cn(
                  "mb-2 flex flex-col gap-2 rounded-sm border px-2.5 py-2",
                  upload.state === "failed" ? "border-destructive/40" : "border-border",
                )}
              >
                <div className="flex items-center gap-2.5">
                  <ReferenceVideoThumb file={upload.file} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] text-foreground">
                      {upload.file.name}
                    </span>
                    {upload.state === "failed" ? (
                      <span
                        role="alert"
                        data-testid="generate-scripts-reference-error"
                        className="mt-0.5 block text-xs text-destructive"
                      >
                        {upload.error}
                      </span>
                    ) : (
                      <span className="mt-0.5 block text-xs text-muted-foreground" role="status">
                        {upload.state === "confirming"
                          ? t(
                              "personaOpportunities.generateScripts.reference.confirming",
                              "Finishing up…",
                            )
                          : uploadBusy
                            ? t("personaOpportunities.generateScripts.reference.uploadingPercent", {
                                percent: Math.round(upload.progress),
                                defaultValue: "Uploading — {{percent}}%",
                              })
                            : upload.state === "ready"
                              ? t(
                                  "personaOpportunities.generateScripts.reference.uploaded",
                                  "Uploaded",
                                )
                              : t(
                                  "personaOpportunities.generateScripts.reference.uploading",
                                  "Uploading reference video…",
                                )}
                      </span>
                    )}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t(
                      "personaOpportunities.generateScripts.reference.clearVideo",
                      "Remove reference video",
                    )}
                    onClick={handleClearVideo}
                  >
                    <Icon name="x" size="sm" />
                  </Button>
                </div>
                {uploadBusy ? <UploadProgressBar percent={upload.progress} /> : null}
              </div>
            ) : null}
            <DashedAction
              testId="generate-scripts-reference-upload"
              icon="plus"
              disabled={isSubmitting}
              onClick={() => videoInputRef.current?.click()}
            >
              {upload.file
                ? t(
                    "personaOpportunities.generateScripts.reference.replaceVideo",
                    "Replace reference video",
                  )
                : t(
                    "personaOpportunities.generateScripts.reference.addVideo",
                    "Add reference video",
                  )}
            </DashedAction>
            {referenceError && upload.state !== "failed" ? (
              <p
                role="alert"
                data-testid="generate-scripts-reference-error"
                className="mt-2 text-xs text-destructive"
              >
                {referenceError}
              </p>
            ) : null}
          </section>

          <section className="mt-3.5 border-t border-border pt-3.5">
            <FieldLabel
              label={t("personaOpportunities.generateScripts.guidelines.label", "Script guidelines")}
              hint={t(
                "personaOpportunities.generateScripts.guidelines.hint",
                "Optional · direction, hooks, talking points, or constraints to apply to everything Adge generates.",
              )}
            />
            <Textarea
              data-testid="generate-scripts-guidelines"
              value={guidelines}
              maxLength={PERSONA_SCRIPT_GUIDELINES_MAX_CHARS}
              onChange={(event) => setGuidelines(event.target.value)}
              placeholder={t(
                "personaOpportunities.generateScripts.guidelines.placeholder",
                "e.g., 'Open with a question hook. Keep scripts under 30s. Always mention the 60-day guarantee. Avoid medical claims...'",
              )}
              className="min-h-[72px] resize-y text-[13px] leading-relaxed"
            />
            <input
              ref={guidelinesInputRef}
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(event) => {
                void handleGuidelinesFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <DashedAction
              className="mt-2"
              icon="upload"
              disabled={isExtracting || isSubmitting}
              onClick={() => guidelinesInputRef.current?.click()}
            >
              {isExtracting
                ? t(
                    "personaOpportunities.generateScripts.guidelines.extracting",
                    "Reading guidelines file…",
                  )
                : t(
                    "personaOpportunities.generateScripts.guidelines.upload",
                    "Upload PDF, DOC, or TXT",
                  )}
            </DashedAction>
            {guidelinesError ? (
              <p role="alert" className="mt-1.5 text-xs text-destructive">
                {guidelinesError}
              </p>
            ) : null}
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {t(
                "personaOpportunities.generateScripts.guidelines.footer",
                "Read into the guidelines above — not sent to creators.",
              )}
            </p>
          </section>

          {wantScript ? (
            <section className="mt-3.5 flex flex-wrap items-end justify-between gap-4 border-t border-border pt-3.5">
              <div>
                <FieldLabel
                  label={t("personaOpportunities.generateScripts.perAngle.label", "Scripts per angle")}
                  hint={t("personaOpportunities.generateScripts.perAngle.hint", "Max 10 per angle.")}
                />
                <div className="inline-flex overflow-hidden rounded-sm border border-input-border">
                  <button
                    type="button"
                    className="grid size-[34px] place-items-center border-r border-border text-foreground hover:bg-muted disabled:opacity-40"
                    aria-label={t(
                      "personaOpportunities.generateScripts.perAngle.decrease",
                      "Decrease scripts per angle",
                    )}
                    disabled={scriptsPerAngle <= MIN_SCRIPTS_PER_ANGLE}
                    onClick={() => setScriptsPerAngle((n) => Math.max(MIN_SCRIPTS_PER_ANGLE, n - 1))}
                  >
                    <Icon name="minus" size="sm" />
                  </button>
                  <span
                    data-testid="generate-scripts-per-angle"
                    className="grid w-[52px] place-items-center text-sm font-medium"
                  >
                    {scriptsPerAngle}
                  </span>
                  <button
                    type="button"
                    className="grid size-[34px] place-items-center border-l border-border text-foreground hover:bg-muted disabled:opacity-40"
                    aria-label={t(
                      "personaOpportunities.generateScripts.perAngle.increase",
                      "Increase scripts per angle",
                    )}
                    disabled={scriptsPerAngle >= MAX_SCRIPTS_PER_ANGLE}
                    onClick={() => setScriptsPerAngle((n) => Math.min(MAX_SCRIPTS_PER_ANGLE, n + 1))}
                  >
                    <Icon name="plus" size="sm" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[19px] font-semibold leading-none text-foreground">
                  {scriptCount}
                </div>
                <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("personaOpportunities.generateScripts.perAngle.totalCaption", "scripts total")}
                </div>
              </div>
            </section>
          ) : null}

          {wantBrief ? (
            <p className="mt-3 text-xs text-muted-foreground">
              {t(
                wantScript
                  ? "personaOpportunities.generateScripts.perAngle.briefNotePlus"
                  : "personaOpportunities.generateScripts.perAngle.briefNote",
                {
                  count: briefCount,
                  defaultValue: wantScript
                    ? "Plus {{count}} briefs — one per angle, each with 4 hook options to choose from."
                    : "{{count}} briefs — one per angle, each with 4 hook options to choose from.",
                },
              )}
            </p>
          ) : null}
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[34px] bg-gradient-to-b from-transparent to-background"
        />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border bg-muted px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            className="h-[42px] px-3.5"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            data-testid="generate-scripts-submit"
            iconStart="sparkles"
            className="h-[42px] px-5 font-semibold"
            disabled={generateDisabled}
            onClick={handleGenerate}
          >
            {submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReferenceVideoThumb({ file }: { file: File }) {
  const { t } = useTranslation();
  const url = useObjectUrl(file.size > 0 ? file : null);

  return (
    <span className="h-[34px] w-[26px] shrink-0 overflow-hidden rounded-[3px] bg-muted">
      {url ? (
        <video
          data-testid="generate-scripts-reference-thumb"
          src={url}
          muted
          playsInline
          preload="metadata"
          aria-label={t(
            "personaOpportunities.generateScripts.reference.uploadThumbnailAlt",
            "Reference video thumbnail",
          )}
          className="size-full object-cover"
        />
      ) : null}
    </span>
  );
}

function UploadProgressBar({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, Math.round(percent)));
  return (
    <div
      data-testid="generate-scripts-reference-progress"
      className="h-1 w-full overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function FieldLabel({
  label,
  hint,
  required,
}: {
  label: string;
  hint: string;
  required?: boolean;
}) {
  return (
    <div className="mb-2">
      <h3 className="text-[13px] font-medium text-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function DashedAction({
  children,
  icon,
  onClick,
  disabled,
  testId,
  className,
}: {
  children: React.ReactNode;
  icon: "plus" | "upload";
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-sm border-[1.5px] border-dashed border-input-border px-2.5 py-2.5 text-[13px] text-foreground transition-colors hover:bg-muted disabled:opacity-40",
        className,
      )}
    >
      <Icon name={icon} size="sm" />
      {children}
    </button>
  );
}

function OutputTypeCard({
  checked,
  onCheckedChange,
  title,
  hint,
  testId,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  title: string;
  hint: string;
  testId: string;
}) {
  return (
    <div
      role="checkbox"
      tabIndex={0}
      data-testid={testId}
      aria-checked={checked}
      onClick={() => onCheckedChange(!checked)}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          onCheckedChange(!checked);
        }
      }}
      className={cn(
        "flex min-w-[200px] flex-1 cursor-pointer items-start gap-2.5 rounded-sm p-3 text-left transition-colors",
        checked
          ? "border-[1.5px] border-primary bg-primary/5"
          : "border border-border hover:bg-muted/60",
      )}
    >
      <Checkbox
        checked={checked}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none mt-px"
      />
      <span className="min-w-0">
        <span
          className={cn(
            "block text-[13px] font-semibold",
            checked ? "text-primary" : "text-foreground",
          )}
        >
          {title}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{hint}</span>
      </span>
    </div>
  );
}
